import { MapBoxGL } from '../MapBoxGL'
import { Expression } from 'mapbox-gl'

import { IStationService } from '@/services/station/IStationService'
import { SelectionEventArgs } from '@/services/station/shared/SelectionManager'
import { ServiceLocator } from '@/services/ServiceLocator'

import { AnyLayer, EventData, MapLayerMouseEvent, MapLayerTouchEvent, SymbolLayer } from 'mapbox-gl'

import { Station } from '@/model/Station'
import { City } from '@/model/City'
import { BaseController } from './BaseController'
import { IController } from './IController'
import { IControllerParent } from './IControllerParent'
import { IMapService } from '@/services/map/IMapService'
import { TimeStep } from '@/model/TimeStep'
import { StationMapSourceManager } from './shared/StationMapSourceManager'
import { MarkerEventManager } from './shared/MarkerEventManager'

/**
 * Handle user interactions related to stations markers
 */
export class StationController extends BaseController implements IController {
  private stationService: IStationService
  private unselectedStations: Map<number, Station> = new Map()
  private selectedStations: Map<number, Station> = new Map()
  private stationsDataMap: Map<number, boolean> = new Map() // Cache hasData for each station
  private waterLegend: any = null // Cache water legend

  private city: City | null = null
  private handler = async (evt: SelectionEventArgs<Station>) => await this.updateFeatureState(evt)

  // Utility managers
  private sourceManager = new StationMapSourceManager('stations')
  private eventManager = new MarkerEventManager()

  private getIconExpression(): Expression {
    return [
      'case',
      ['all', ['!', ['in', 'Streamflow', ['get', 'types']]], ['in', 'Water level', ['get', 'types']]],
      'water-level-marker',
      ['all', ['!', ['in', 'Water level', ['get', 'types']]], ['in', 'Streamflow', ['get', 'types']]],
      'water-flow-marker',
      'combined-marker',
    ]
  }

  constructor(parent: IControllerParent, mapBoxGL: MapBoxGL, mapService: IMapService, serviceLocator: ServiceLocator) {
    super(parent, mapBoxGL, mapService)
    this.stationService = serviceLocator.get<IStationService>('stations')
    this.stationService.stationSelectionChanged.subscribe(this.handler)
  }

  beforeDestroy(): void {
    this.stationService.stationSelectionChanged.unsubscribe(this.handler)
  }

  async load(city: City, timeStep?: TimeStep, zoomOnSource = false, scale = 500): Promise<boolean> {
    if (!timeStep) {
      return false
    }
    if (this.city == null || this.city.getName() !== timeStep.snapshot.city.getName()) {
      this.city = timeStep.snapshot.city
      await this.reloadStations(timeStep)
    }
    return true
  }

  private async reloadStations(timeStep: TimeStep): Promise<void> {
    if (this.city == null) return
    this.unselectedStations.clear()
    this.selectedStations.clear()
    this.stationsDataMap.clear()

    const timestamp = timeStep.timestamp.toUTC()

    // Load water legend and cache it
    this.waterLegend = await this.stationService.getWaterLegend()

    // Get stations with data availability
    const stationsWithData = await this.stationService.getStationsWithData(this.city.getName(), timestamp)
    const filteredStationsWithData = stationsWithData.filter(
      ({ station }) => station.stationTypes.includes('Streamflow') || station.stationTypes.includes('Water level'),
    )

    // Store hasData for each station
    filteredStationsWithData.forEach(({ station, hasData }) => {
      this.stationsDataMap.set(station.no, hasData)
      if (!this.selectedStations.has(station.no)) {
        this.unselectedStations.set(station.no, station)
      }
    })

    // Separate into selected and unselected
    const unselectedWithData = filteredStationsWithData.filter(({ station }) => !this.selectedStations.has(station.no))
    const selectedWithData = filteredStationsWithData.filter(({ station }) => this.selectedStations.has(station.no))

    // Get selected IDs
    const selectedIds = new Set(this.selectedStations.keys())

    // Update sources with colored features
    this.sourceManager.updateSources(
      this.mapBoxGL,
      this.stationService.mapToFeatureCollectionWithColors(unselectedWithData, selectedIds, this.waterLegend),
      this.stationService.mapToFeatureCollectionWithColors(selectedWithData, selectedIds, this.waterLegend),
      this.stationService.mapToFeatureCollectionWithColors(filteredStationsWithData, selectedIds, this.waterLegend),
    )

    // Register click handlers (eventManager handles cleanup automatically)
    this.eventManager.registerClickHandler(
      this.mapBoxGL,
      `${this.mapBoxGL.applicationPrefix}-unselectedstations`,
      async (evt: (MapLayerMouseEvent | MapLayerTouchEvent) & EventData) => this.onMarkerClicked(evt),
    )
    this.eventManager.registerClickHandler(
      this.mapBoxGL,
      `${this.mapBoxGL.applicationPrefix}-selectedstations`,
      async (evt: (MapLayerMouseEvent | MapLayerTouchEvent) & EventData) => this.onMarkerClicked(evt),
    )
  }

  async onMarkerClicked(e: (MapLayerMouseEvent | MapLayerTouchEvent) & EventData): Promise<void> {
    if (e.features && e.features[0] && e.features[0].properties) {
      const featureProperties = e.features[0].properties
      const no = featureProperties['no']
      const stationSource = this.unselectedStations.has(no) ? this.unselectedStations : this.selectedStations

      await this.stationService.toggleStation(stationSource.get(no) as Station)
    }
  }

  async updateFeatureState(args: SelectionEventArgs<Station>): Promise<void> {
    const isAddAction = args.action === 'add'

    if (isAddAction) {
      args.items.forEach((s) => this.selectedStations.set(s.no, s))
      args.items.forEach((s) => this.unselectedStations.delete(s.no))
    } else {
      args.items.forEach((s) => this.selectedStations.delete(s.no))
      args.items.forEach((s) => this.unselectedStations.set(s.no, s))
    }

    // Use cached data and legend for colored features
    const selectedIds = new Set(this.selectedStations.keys())

    // Convert stations to StationWithData format
    const unselectedWithData = [...this.unselectedStations.values()].map((station) => ({
      station,
      hasData: this.stationsDataMap.get(station.no) || false,
    }))
    const selectedWithData = [...this.selectedStations.values()].map((station) => ({
      station,
      hasData: this.stationsDataMap.get(station.no) || false,
    }))
    const allWithData = [...unselectedWithData, ...selectedWithData]

    this.sourceManager.updateSources(
      this.mapBoxGL,
      this.stationService.mapToFeatureCollectionWithColors(unselectedWithData, selectedIds, this.waterLegend),
      this.stationService.mapToFeatureCollectionWithColors(selectedWithData, selectedIds, this.waterLegend),
      this.stationService.mapToFeatureCollectionWithColors(allWithData, selectedIds, this.waterLegend),
    )
  }

  clearCities(): void {
    this.stationService.clearStations()
  }

  getLayers(): AnyLayer[] {
    return [
      {
        id: `${this.mapBoxGL.applicationPrefix}-unselectedstations`,
        type: 'symbol',
        source: this.sourceManager.unselectedSource,
        minzoom: 3,
        layout: {
          'icon-image': this.getIconExpression(),
          'icon-allow-overlap': true,
          'text-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-offset': [0, -6],
          'text-ignore-placement': true,
          'text-size': 0,
        },
        paint: {
          'icon-color': ['coalesce', ['get', 'color'], '#4682B4'],
        },
      } as SymbolLayer,
      {
        id: `${this.mapBoxGL.applicationPrefix}-selectedstations`,
        type: 'symbol',
        source: this.sourceManager.selectedSource,
        minzoom: 3,
        layout: {
          'icon-image': this.getIconExpression(),
          'icon-allow-overlap': true,
          'text-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-offset': [0, -6],
          'text-ignore-placement': true,
          'text-size': 0,
        },
        paint: {
          'icon-color': ['coalesce', ['get', 'color'], '#7753EB'],
        },
      } as SymbolLayer,
      {
        id: `${this.mapBoxGL.applicationPrefix}-stationstext`,
        type: 'symbol',
        source: this.sourceManager.allSource,
        minzoom: 3,
        layout: {
          'text-field': ['get', 'name_no'],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-offset': [0, 1.5],
          'text-anchor': 'top',
          'text-size': 10,
          'icon-size': 0.01,
          'icon-allow-overlap': false,
          'text-allow-overlap': false,
          'icon-ignore-placement': false,
          'text-ignore-placement': false,
        },
        paint: {
          'text-halo-color': '#ffffff',
          'text-halo-blur': 1,
          'text-halo-width': 2,
        },
      } as SymbolLayer,
    ]
  }

  getSources(): string[] {
    return this.sourceManager.getAllSourceIds()
  }

  getCursors(): Map<string, string> {
    return new Map([
      [`${this.mapBoxGL.applicationPrefix}-selectedstations`, 'pointer'],
      [`${this.mapBoxGL.applicationPrefix}-unselectedstations`, 'pointer'],
      [`${this.mapBoxGL.applicationPrefix}-stationstext`, 'pointer'],
    ])
  }

  getSymbols(): Map<string, { url: string; sdf: boolean }> {
    return new Map([
      [
        'water-level-marker',
        {
          url: require('@/assets/water level marker.png'),
          sdf: true,
        },
      ],
      [
        'water-flow-marker',
        {
          url: require('@/assets/water flow marker.png'),
          sdf: true,
        },
      ],
      [
        'combined-marker',
        {
          url: require('@/assets/combined marker.png'),
          sdf: true,
        },
      ],
    ])
  }

  hasStation(): boolean {
    return this.selectedStations.size > 0 || this.unselectedStations.size > 0
  }
}
